/**
 * ZoneScopePanel - the "Edit zones" panel: one checkbox per template zone plus
 * a per-zone robot-count select, saved as this project's zone scope.
 */
import { useRef, useState } from 'react';
import {
  BTN_GHOST,
  BTN_PRIMARY,
  GREEN,
  INPUT,
  POPOVER_BOX,
  SHEET_BACKDROP,
  SHEET_BOX,
  SHEET_HANDLE,
  useDismiss,
  useIsNarrow,
  zoneItemCount,
} from './svmModel';

// "Edit zones" panel - pick which template zones are in this project's build
// ("zone scope") and how many robots the project runs in each. Same
// fixed-position / outside-click / Escape pattern as CellPopover. Save PUTs
// via onSave (throws on failure → inline error). `selected` is a Map of
// zone key → robot count; scope entries with robots: null (legacy) preselect
// the full template count.
export default function ZoneScopePanel({ rect, zones, scope, onClose, onSave }) {
  const narrow = useIsNarrow(); // phone → bottom sheet, like CellPopover
  const fullOf = (key) => {
    const z = zones.find((zz) => zz.key === key);
    return z ? (z.robots || []).length : 0;
  };
  const [selected, setSelected] = useState(() => {
    const m = new Map();
    const entries = scope || zones.map((z) => ({ key: z.key, robots: null }));
    for (const s of entries) {
      const full = fullOf(s.key);
      m.set(s.key, s.robots == null ? full : Math.max(1, Math.min(s.robots, full || s.robots)));
    }
    return m;
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const ref = useRef(null);

  useDismiss(ref, onClose);

  const toggle = (key) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(key)) next.delete(key);
      else next.set(key, fullOf(key));
      return next;
    });
  };
  const setRobots = (key, n) => setSelected((prev) => new Map(prev).set(key, n));

  const save = async () => {
    setBusy(true);
    setErr(null);
    try {
      // Template order preserved: filter the template list, not the Map.
      await onSave(zones.filter((z) => selected.has(z.key)).map((z) => ({ key: z.key, robots: selected.get(z.key) })));
      onClose();
    } catch (e) {
      setErr(e.message || 'Save failed');
      setBusy(false);
    }
  };

  const W = 320;
  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const left = Math.max(8, Math.min(rect.left - W + 90, vw - W - 12));
  const below = rect.bottom + 8;
  const top = below + 340 > vh ? Math.max(8, rect.top - 348) : below;

  const boxStyle = narrow ? SHEET_BOX : { ...POPOVER_BOX, left, top, width: W };

  return (
    <>
      {narrow && <div style={SHEET_BACKDROP} onClick={onClose} />}
      <div ref={ref} style={boxStyle}>
        {narrow && <div style={SHEET_HANDLE} />}
        <div style={{ fontSize: 11.5, fontWeight: 800, color: '#0D0D0D', marginBottom: 4 }}>Zones in this project</div>
        <div style={{ fontSize: 10.5, color: '#999', lineHeight: 1.4, marginBottom: 4 }}>
          Only the zones in this project's build - every project ships a different set.
        </div>
        <div style={{ fontSize: 10.5, color: '#999', lineHeight: 1.4, marginBottom: 10 }}>
          Robot count = how many robots this project actually runs in that zone.
        </div>
        {err && <div style={{ fontSize: 11, color: '#B71C1C', fontWeight: 600, marginBottom: 8 }}>{err}</div>}
        {zones.map((z) => {
          const full = (z.robots || []).length;
          const checked = selected.has(z.key);
          return (
            <div
              key={z.key}
              style={{ display: 'flex', alignItems: 'center', gap: 8, padding: narrow ? '8px 0' : '4px 0', fontSize: 11.5, color: '#333' }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', flex: 1, minWidth: 0 }}>
                <input type="checkbox" checked={checked} onChange={() => toggle(z.key)} style={{ accentColor: GREEN, margin: 0 }} />
                <span style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{z.key}</span>
              </label>
              {checked && full > 0 && (
                <select
                  value={selected.get(z.key)}
                  onChange={(e) => setRobots(z.key, Number(e.target.value))}
                  style={{ ...INPUT, width: 'auto', fontSize: 10.5, padding: '3px 4px' }}
                  title="How many robots this project actually runs in this zone"
                >
                  {z.robots.map((_, i) => (
                    <option key={i + 1} value={i + 1}>
                      {i + 1} of {full} robots
                    </option>
                  ))}
                </select>
              )}
              <span style={{ fontSize: 10, color: '#AAA', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                {zoneItemCount(z)} items
              </span>
            </div>
          );
        })}
        <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
          <button
            type="button"
            style={{
              ...BTN_PRIMARY,
              fontSize: 11,
              padding: narrow ? '10px 16px' : '6px 12px',
              opacity: busy || selected.size === 0 ? 0.5 : 1,
            }}
            disabled={busy || selected.size === 0}
            onClick={save}
          >
            {busy ? 'Saving…' : 'Save'}
          </button>
          <button type="button" style={{ ...BTN_GHOST, fontSize: 11, padding: narrow ? '10px 16px' : '6px 12px' }} onClick={onClose}>
            Cancel
          </button>
        </div>
      </div>
    </>
  );
}
